/**
 * BFS/Dijkstra sound flood-fill on the tile grid. Sound propagates outward
 * from an origin, paying per-tile-entry cost until its remaining loudness
 * hits zero. See CLAUDE.md > Sound.
 *
 * Costs are pluggable; the default `defaultSoundCost` reads
 * CONFIG.sound.cost so all tunables live in one place (architecture rule 5).
 *
 * Result is a Map<tileIdx, residualLoudness>. A tile appears in the map iff
 * the sound reached it with residual >= 0.
 *
 * Headless — no PixiJS. See CLAUDE.md architecture rule 4.
 */

import { CONFIG } from '../config';
import { inBounds, tileAt, type MapData } from './map';
import { TILE_TRAITS, TileType } from './tiles';

export function defaultSoundCost(t: TileType): number {
  // Void is impassable to sound; every other tile names its cost key in the
  // trait table, and the value is tuned in CONFIG.sound.cost.
  if (t === TileType.Void) return Infinity;
  return CONFIG.sound.cost[TILE_TRAITS[t].soundCostKey];
}

/**
 * What fraction of the remaining loudness gets THROUGH this tile, as opposed to
 * how much distance crossing it costs. A barrier is not a long room: whatever
 * hits a shut door, a tenth of it comes out the other side.
 */
export function defaultTransmission(t: TileType): number {
  const tx = CONFIG.sound.transmission;
  switch (t) {
    case TileType.DoorClosed: return tx.doorClosed;
    case TileType.Wall: return tx.wall;
    case TileType.CubiclePartition: return tx.cubicle;
    default: return 1;
  }
}

/** 8-neighbor with √2 diagonal cost → nearly-circular flood shape instead
 *  of the boxy Manhattan diamond a 4-neighbor BFS produces. */
const DIRS: ReadonlyArray<readonly [number, number, number]> = [
  [1, 0, 1],
  [-1, 0, 1],
  [0, 1, 1],
  [0, -1, 1],
  [1, 1, 1.41421356],
  [1, -1, 1.41421356],
  [-1, 1, 1.41421356],
  [-1, -1, 1.41421356],
];

/**
 * @returns a Map keyed by `y * map.width + x` → remaining loudness (>= 0)
 *          for every tile the sound reaches, including the origin. Includes
 *          a wall-reflection pass: each wall/partition tile in the flood
 *          spawns a secondary echo source at 35% of its residual, so tiles
 *          adjacent to a wall get a small bump. Reads visually as "sound
 *          bouncing" in corridors and small rooms.
 */
export function floodSound(
  originX: number,
  originY: number,
  initialLoudness: number,
  map: MapData,
  costFor: (t: TileType) => number = defaultSoundCost,
  transmissionFor: (t: TileType) => number = defaultTransmission,
): Map<number, number> {
  const residual = new Map<number, number>();
  if (!inBounds(map, originX, originY) || initialLoudness <= 0) return residual;

  // Dijkstra on REMAINING loudness rather than accumulated cost, because a
  // barrier attenuates multiplicatively (a door passes a tenth of whatever
  // reaches it) while distance subtracts. Expanding the loudest frontier first
  // keeps the "best path wins" property that made the cost version correct.
  const originIdx = originY * map.width + originX;
  residual.set(originIdx, initialLoudness);

  const heap = new MaxHeap<[number, number, number]>((a, b) => b[0] - a[0]);
  heap.push([initialLoudness, originX, originY]);

  while (heap.size() > 0) {
    const top = heap.pop();
    if (!top) break;
    const [loud, x, y] = top;
    const idx = y * map.width + x;
    const known = residual.get(idx);
    if (known !== undefined && loud < known) continue; // superseded by a louder path

    for (const [dx, dy, mult] of DIRS) {
      const nx = x + dx;
      const ny = y + dy;
      if (!inBounds(map, nx, ny)) continue;
      const t = tileAt(map, nx, ny);
      const step = costFor(t) * mult;
      if (!Number.isFinite(step)) continue;
      const next = (loud - step) * transmissionFor(t);
      if (next <= 0) continue;
      const nidx = ny * map.width + nx;
      const prior = residual.get(nidx);
      if (prior === undefined || next > prior) {
        residual.set(nidx, next);
        heap.push([next, nx, ny]);
      }
    }
  }

  // -- Reflection pass ----------------------------------------------------
  // Every wall / partition reached by the flood echoes at 35% of its
  // residual. Because reflection sources ARE walls, the echo primarily
  // adds loudness to *floor* tiles adjacent to them — realistic bounce.
  addReflections(residual, map, costFor);
  return residual;
}

function addReflections(
  residual: Map<number, number>,
  map: MapData,
  costFor: (t: TileType) => number,
): void {
  const ECHO_FACTOR = 0.35;
  // Snapshot the wall tiles reached with meaningful residual.
  const echoes: Array<[number, number, number]> = [];
  for (const [idx, r] of residual) {
    if (r < 1) continue;
    const x = idx % map.width;
    const y = Math.floor(idx / map.width);
    const t = tileAt(map, x, y);
    if (t === TileType.Wall || t === TileType.CubiclePartition) {
      echoes.push([x, y, r * ECHO_FACTOR]);
    }
  }
  // Each echo runs a small 1-hop bump over its 8-neighbors.
  for (const [ex, ey, budget] of echoes) {
    for (const [dx, dy, mult] of DIRS) {
      const nx = ex + dx;
      const ny = ey + dy;
      if (!inBounds(map, nx, ny)) continue;
      const step = costFor(tileAt(map, nx, ny)) * mult;
      if (!Number.isFinite(step)) continue;
      const bonus = budget - step;
      if (bonus <= 0) continue;
      const nidx = ny * map.width + nx;
      const cur = residual.get(nidx) ?? 0;
      // Take the max so we don't inflate residuals boundlessly across
      // multiple reflection sources.
      if (bonus > cur) residual.set(nidx, bonus);
    }
  }
}

/** Small binary min-heap. Adequate for phase-2 flood sizes. */
/** Binary heap ordered by the supplied comparator. The sound flood pops the
 *  LOUDEST frontier first (see floodSound), the reflection pass reuses it. */
class MaxHeap<T> {
  private readonly a: T[] = [];
  constructor(private readonly cmp: (a: T, b: T) => number) {}
  size(): number {
    return this.a.length;
  }
  push(v: T): void {
    this.a.push(v);
    this.siftUp(this.a.length - 1);
  }
  pop(): T | undefined {
    if (this.a.length === 0) return undefined;
    const top = this.a[0]!;
    const last = this.a.pop()!;
    if (this.a.length > 0) {
      this.a[0] = last;
      this.siftDown(0);
    }
    return top;
  }
  private siftUp(i: number): void {
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (this.cmp(this.a[i]!, this.a[p]!) < 0) {
        [this.a[i], this.a[p]] = [this.a[p]!, this.a[i]!];
        i = p;
      } else return;
    }
  }
  private siftDown(i: number): void {
    const n = this.a.length;
    while (true) {
      const l = 2 * i + 1;
      const r = 2 * i + 2;
      let smallest = i;
      if (l < n && this.cmp(this.a[l]!, this.a[smallest]!) < 0) smallest = l;
      if (r < n && this.cmp(this.a[r]!, this.a[smallest]!) < 0) smallest = r;
      if (smallest === i) return;
      [this.a[i], this.a[smallest]] = [this.a[smallest]!, this.a[i]!];
      i = smallest;
    }
  }
}
